// Moved from components/project/StreamCreateModal.tsx
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Typography, Alert, CircularProgress, Collapse
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { streamApi } from '@/api/stream';
import { teamApi } from '@/api/team';
import { useAuth } from '@/hooks/useAuth';

interface StreamCreateModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  status: 'RUNNING' | 'PAUSED' | 'WAITING' | 'DONE';
  notes: string;
  team_uuid?: string;
  operator_uuid?: string;
  creator_uuid?: string;
  start_date?: string;
  end_date?: string;
  messages_template?: string; // raw string from textarea
  // advanced fields
  persona?: string;
  prospect_application?: string;
  prospect_brief?: string;
  prospect_operation?: string;
  company_application?: string;
  company_brief?: string;
  company_operation?: string;
  post_filter_criteria?: string;
  territory?: string;
  mls_topic?: string;
  sales_nav_urls?: string[];
  company_names?: string[];
  mls_filters?: any;
  mls_segment_ids?: string[];
  mls_type?: string;
  google_sheet_urls?: string[];
}

interface FormErrors { name?: string; status?: string; }

export const StreamCreateModal: React.FC<StreamCreateModalProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formData, setFormData] = useState<FormData>({ name: '', status: 'WAITING', notes: '' });
  const [errors, setErrors] = useState<FormErrors>({});

  // Load teams where user is owner or manager
  const { data: teamsData } = useQuery({
    queryKey: ['user-teams', currentUser?.key],
    queryFn: () => teamApi.getTeams({ manager_or_owner_uuid: currentUser?.key, max_results: 1000 }),
    enabled: !!currentUser && open,
  });

  const availableTeams = teamsData?.output?.items || [];
  const hasTeams = availableTeams.length > 0;

  // Load team members when team is selected
  const { data: teamRolesData } = useQuery({
    queryKey: ['team-roles', formData.team_uuid],
    queryFn: () => teamApi.getTeamRoles(formData.team_uuid!),
    enabled: !!formData.team_uuid && open,
  });

  const teamMembers = teamRolesData?.output?.items || [];

  // Load all users for admin/staff when no team selected
  const { data: allUsersData } = useQuery({
    queryKey: ['all-users', currentUser?.key],
    queryFn: () => teamApi.searchAllUsers({ max_results: 1000 }),
    enabled: !!currentUser && open && !formData.team_uuid && (currentUser.is_staff || currentUser.is_superuser),
  });

  const allUsers = allUsersData?.output?.items || [];

  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;

  // Get available operators based on context
  const getAvailableOperators = () => {
    if (formData.team_uuid) {
      // If team selected, return team members + owner
      const memberUsers = teamMembers.map((role: any) => role.user).filter(Boolean);
      const team = availableTeams.find((t: any) => t.key === formData.team_uuid);
      if (team?.owner && !memberUsers.find((u: any) => u.key === team.owner?.key)) {
        memberUsers.push(team.owner);
      }
      return memberUsers;
    } else if (isAdmin) {
      // If no team and admin/staff, return all users
      return allUsers;
    }
    // Otherwise, return empty (will default to creator)
    return [];
  };

  // Get available creators based on context
  const getAvailableCreators = () => {
    if (formData.team_uuid) {
      // If team selected, creator can be user himself or team members
      const memberUsers = teamMembers.map((role: any) => role.user).filter(Boolean);
      const team = availableTeams.find((t: any) => t.key === formData.team_uuid);
      if (team?.owner && !memberUsers.find((u: any) => u.key === team.owner?.key)) {
        memberUsers.push(team.owner);
      }
      // Always include current user (admin) as option
      if (currentUser && !memberUsers.find((u: any) => u.key === currentUser.key)) {
        memberUsers.push(currentUser);
      }
      return memberUsers;
    } else {
      // If no team selected, creator can be user himself or selected operator
      const creators = [currentUser].filter(Boolean);
      if (formData.operator_uuid && formData.operator_uuid !== currentUser?.key) {
        const operator = allUsers.find((u: any) => u.key === formData.operator_uuid);
        if (operator && !creators.find((u: any) => u.key === operator.key)) {
          creators.push(operator as any);
        }
      }
      return creators;
    }
  };

  const availableOperators = getAvailableOperators();
  const availableCreators = getAvailableCreators();

  useEffect(() => {
    if (!open) {
      setFormData({ name: '', status: 'WAITING', notes: '', team_uuid: undefined, start_date: undefined, end_date: undefined });
      setErrors({});
      setShowAdvanced(false);
    }
  }, [open]);

  // Reset operator when team changes
  useEffect(() => {
    if (formData.team_uuid) {
      // When team is selected, default operator to creator (current user)
      if (!formData.operator_uuid || !availableOperators.find((u: any) => u.key === formData.operator_uuid)) {
        setFormData(prev => ({ ...prev, operator_uuid: currentUser?.key }));
      }
    } else {
      // When no team, default operator to creator
      if (!formData.operator_uuid || (!(currentUser?.is_staff || currentUser?.is_superuser) && formData.operator_uuid !== currentUser?.key)) {
        setFormData(prev => ({ ...prev, operator_uuid: currentUser?.key }));
      }
    }
  }, [formData.team_uuid, availableOperators.length]);

  // Update available creators when operator changes (for no-team case)
  useEffect(() => {
    if (!formData.team_uuid && isAdmin) {
      // When no team and admin, creator options should include current user and selected operator
      // Just ensure creator is valid if operator changed
      const creators = [currentUser].filter(Boolean);
      if (formData.operator_uuid && formData.operator_uuid !== currentUser?.key) {
        const operator = allUsers.find((u: any) => u.key === formData.operator_uuid);
        if (operator && !creators.find((u: any) => u.key === operator.key)) {
          creators.push(operator as any);
        }
      }
      if (formData.creator_uuid && !creators.find((u: any) => u.key === formData.creator_uuid)) {
        // If current creator is no longer valid, reset to current user
        setFormData(prev => ({ ...prev, creator_uuid: currentUser?.key }));
      }
    }
  }, [formData.operator_uuid, formData.team_uuid, allUsers, currentUser, isAdmin]);

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!formData.name.trim()) e.name = 'Experiment name is required';
    if (!formData.status) e.status = 'Status is required';
    return e;
  };

  const createMutation = useMutation({
    mutationFn: () => {
      // Determine operator: use formData.operator_uuid if set, otherwise default to creator
      const operatorUuid = formData.operator_uuid || formData.creator_uuid || currentUser?.key;
      
      return streamApi.createStream({
        team_uuid: formData.team_uuid,
      name: formData.name.trim(),
      status: formData.status,
      notes: formData.notes || undefined,
        operator_uuid: operatorUuid,
        creator_uuid: formData.creator_uuid,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
      messages_template: formData.messages_template,
      persona: formData.persona,
      prospect_application: formData.prospect_application,
      prospect_brief: formData.prospect_brief,
      prospect_operation: formData.prospect_operation,
      company_application: formData.company_application,
      company_brief: formData.company_brief,
      company_operation: formData.company_operation,
      post_filter_criteria: formData.post_filter_criteria,
      territory: formData.territory,
      mls_topic: formData.mls_topic,
      sales_nav_urls: formData.sales_nav_urls,
      company_names: formData.company_names,
      mls_filters: formData.mls_filters,
      mls_segment_ids: formData.mls_segment_ids,
      mls_type: formData.mls_type,
      google_sheet_urls: formData.google_sheet_urls,
      });
    },
    onSuccess: (resp: any) => {
      queryClient.invalidateQueries({ queryKey: ['streams'] });
      queryClient.invalidateQueries({ queryKey: ['experiments'] });
      onClose();
      const streamKey = resp?.output?.key || resp?.output?.uuid || resp?.output?.id;
      if (streamKey) {
        navigate(`/campaigns/${streamKey}`);
      }
    },
  });

  const handleChange = (field: keyof FormData) => (e: any) => {
    const v = e?.target ? e.target.value : e;
    setFormData(prev => ({ ...prev, [field]: v }));
    if (errors[field as keyof FormErrors]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create Experiment</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Experiment Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || 'Name for this experiment'}
            disabled={createMutation.isPending}
            required
          />

          {/* Team Selection - only show if user has teams */}
          {hasTeams && (
            <FormControl fullWidth>
              <InputLabel id="team-label">Team (Optional)</InputLabel>
              <Select
                labelId="team-label"
                label="Team (Optional)"
                value={formData.team_uuid || ''}
                onChange={handleChange('team_uuid')}
                disabled={createMutation.isPending}
              >
                <MenuItem value="">None</MenuItem>
                {availableTeams.map((team: any) => (
                  <MenuItem key={team.key} value={team.key}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Creator Selection - only for admin/staff */}
          {isAdmin && availableCreators.length > 0 && (
            <FormControl fullWidth>
              <InputLabel id="creator-label">Creator</InputLabel>
              <Select
                labelId="creator-label"
                label="Creator"
                value={formData.creator_uuid || currentUser?.key || ''}
                onChange={handleChange('creator_uuid')}
                disabled={createMutation.isPending}
              >
                {availableCreators.map((user: any) => (
                  <MenuItem key={user.key} value={user.key}>
                    {user.name || user.email} {user.key === currentUser?.key ? '(You)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Operator Selection - show if team selected or if admin/staff */}
          {(formData.team_uuid || isAdmin) && availableOperators.length > 0 && (
            <FormControl fullWidth>
              <InputLabel id="operator-label">Operator</InputLabel>
              <Select
                labelId="operator-label"
                label="Operator"
                value={formData.operator_uuid || currentUser?.key || ''}
                onChange={handleChange('operator_uuid')}
                disabled={createMutation.isPending}
              >
                {availableOperators.map((user: any) => (
                  <MenuItem key={user.key} value={user.key}>
                    {user.name || user.email} {user.key === currentUser?.key ? '(You)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth error={!!errors.status}>
            <InputLabel id="stream-status-label">Status *</InputLabel>
            <Select labelId="stream-status-label" label="Status *" value={formData.status} onChange={handleChange('status')} disabled={createMutation.isPending}>
              <MenuItem value="RUNNING">Running</MenuItem>
              <MenuItem value="PAUSED">Paused</MenuItem>
              <MenuItem value="WAITING">Waiting</MenuItem>
              <MenuItem value="DONE">Done</MenuItem>
            </Select>
            {errors.status && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.status}
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Notes"
            multiline
            minRows={3}
            value={formData.notes}
            onChange={handleChange('notes')}
            disabled={createMutation.isPending}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={formData.start_date || ''}
              onChange={handleChange('start_date')}
              disabled={createMutation.isPending}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={formData.end_date || ''}
              onChange={handleChange('end_date')}
              disabled={createMutation.isPending}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <Button size="small" onClick={() => setShowAdvanced(s => !s)} sx={{ alignSelf: 'flex-start' }}>
            {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
          </Button>
          <Collapse in={showAdvanced}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField label="Persona" multiline minRows={3} value={formData.persona || ''} onChange={handleChange('persona')} />
              <TextField label="Prospect Application" multiline minRows={3} value={formData.prospect_application || ''} onChange={handleChange('prospect_application')} />
              <TextField label="Prospect Brief" multiline minRows={3} value={formData.prospect_brief || ''} onChange={handleChange('prospect_brief')} />
              <TextField label="Prospect Operation" multiline minRows={3} value={formData.prospect_operation || ''} onChange={handleChange('prospect_operation')} />
              <TextField label="Company Application" multiline minRows={3} value={formData.company_application || ''} onChange={handleChange('company_application')} />
              <TextField label="Company Brief" multiline minRows={3} value={formData.company_brief || ''} onChange={handleChange('company_brief')} />
              <TextField label="Company Operation" multiline minRows={3} value={formData.company_operation || ''} onChange={handleChange('company_operation')} />
              <TextField label="Post Filter Criteria" multiline minRows={3} value={formData.post_filter_criteria || ''} onChange={handleChange('post_filter_criteria')} />
              <TextField label="Territory" multiline minRows={3} value={formData.territory || ''} onChange={handleChange('territory')} />
              <TextField label="MLS Topic" multiline minRows={3} value={formData.mls_topic || ''} onChange={handleChange('mls_topic')} />
              <FormControl fullWidth>
                <InputLabel id="mls-type-label">MLS Type</InputLabel>
                <Select labelId="mls-type-label" label="MLS Type" value={formData.mls_type || ''} onChange={handleChange('mls_type')}>
                  <MenuItem value="people">Inventor</MenuItem>
                  <MenuItem value="patent">Patent</MenuItem>
                  <MenuItem value="author">Author</MenuItem>
                  <MenuItem value="paper">Paper</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Messages Template"
                multiline
                minRows={6}
                value={formData.messages_template || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, messages_template: e.target.value }))}
              />
            </Box>
          </Collapse>

          {createMutation.isError && (
            <Alert severity="error">Failed to create experiment.</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createMutation.isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            const v = validate();
            if (Object.keys(v).length) { setErrors(v); return; }
            createMutation.mutate();
          }}
          startIcon={createMutation.isPending ? <CircularProgress size={20} /> : undefined}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
